/* @flow */
import _ from 'lodash';

import AffiliationStore from 'stores/AffiliationStore';
import AuthActions, { onSessionCreated } from 'auth/AuthActions';
import { RESET_PASSWORD_TOKEN_COOKIE } from 'constants/AuthConstants';
import cookies from 'lib/Cookies';
import CurrentUser from 'auth/CurrentUser';
import DeviceStore from 'stores/DeviceStore';
import DeviceActions from 'actions/DeviceActions';
import GroupActions from 'groups/actions/GroupActions';
import GroupStore from 'groups/stores/GroupStore';
import OrganizationActions from 'actions/OrganizationActions';
import ToastActions from 'actions/ToastActions';
import User from 'entities/User';
import { dispatcher } from 'lib/Dispatcher';
import { genericCatch } from 'lib/promise';

import { ActionTypes } from './UserConstants';

const editPassword = (values: {current_password?: string, password: string}) => {
  const { password } = values;

  const signInWithDevice = () => {
    const devices = DeviceStore.getDevices();
    const emailOrPhoneDevice = devices.find(d => d.getType() === 'sms' || d.getType() === 'email');
    return AuthActions.signIn(emailOrPhoneDevice.getEndpoint(), password, false).then((user) => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
    });
  };

  const updatePassword = () => {
    return User.update(values).then(signInWithDevice);
  };

  return DeviceActions.loadDevices().then(updatePassword);
};

const UserActions = {
  // TODO does this require a token or not?
  loadCurrentUser(token: ?string) {
    return User.fetch({ token }).then((user) => {
      dispatcher.dispatch({
        type: ActionTypes.LOAD_USER,
        user,
      });

      return user;
    });
  },

  update(attrs: Object): Promise<User> {
    return User.update(attrs).then((user) => {
      if (CurrentUser.isSignedIn() &&
        !_.isEqual(CurrentUser.getUser().getOrganizationIds(), user.getOrganizationIds())) {
        GroupActions.loadGroups();
      }

      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });

      return user;
    });
  },

  updateLocal(attrs: Object): void {
    const user = CurrentUser.getUser();

    user.set(attrs);

    dispatcher.dispatch({
      type: ActionTypes.USER_UPDATED,
      user,
    });
  },

  createPassword(password: string) {
    return editPassword({ password });
  },

  changePassword(currentPassword: ?string, newPassword: string) {
    const values = { password: newPassword };
    if (currentPassword) {
      Object.assign(values, { current_password: currentPassword });
    }
    return editPassword(values);
  },

  create(attrs: Object) {
    return User.create(attrs).then(onSessionCreated);
  },

  delete(token: ?string) {
    return User.delete(token).then(() => {
      dispatcher.dispatch({
        type: ActionTypes.USER_DELETED,
      });
    });
  },

  verifyDeleteToken() {
    return User.verifyDeleteToken(...arguments);
  },

  confirm() {
    return User.confirm(...arguments);
  },

  requestConfirmation() {
    return User.requestConfirmation();
  },

  savePreference() {
    const currentUser = CurrentUser.getUser();

    return currentUser.savePreference(...arguments).then(preferences => {
      dispatcher.dispatch({
        type: ActionTypes.PREFERENCES_UPDATED,
        preferences,
      });

      return preferences;
    });
  },

  setRole(role: string): void {
    User.setRole(role).then(user => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
    })
    .catch(genericCatch);
  },

  resetPassword(password: string): Promise<User> {
    const token = cookies.isEnabled() ? cookies.get(RESET_PASSWORD_TOKEN_COOKIE) : null;
    cookies.expire(RESET_PASSWORD_TOKEN_COOKIE);
    return User.resetPassword(password, token);
  },

  setBirthdate(birthdate: Date): Promise<void> {
    return User.setBirthdate(birthdate).then(user => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
    });
  },

  setParentEmail(parentEmail: string) {
    // TODO: Add error handling here
    return User.setParentEmail(parentEmail).then(user => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
    });
  },

  requestForPublicGroups(teacherId: number) {
    return User.requestForPublicGroups(teacherId).then(() => {
      ToastActions.showSuccessToast('Invite sent');
    }).catch(genericCatch);
  },

  reportUser(userId: number, reason: string, notes: string): void {
    User.reportUser(userId, reason, notes).then(() => {
      ToastActions.showSuccessToast('Report submitted');
    }).catch(genericCatch);
  },

  leaveOrg(removingId: number) {
    const schools = AffiliationStore.getMemberOrganizations();
    const onRemoveSuccess = () => {
      const oldSchool = schools.filter(s => s.id === removingId)[0];
      ToastActions.showSuccessToast(`You left ${oldSchool.getName()}`);
      OrganizationActions.removeOrganization(removingId);
      const oldStaffClass = GroupStore.getStaffClassForOrg(removingId);
      if (oldStaffClass) {
        GroupActions.removeGroup(oldStaffClass);
      }
    };

    const schoolIds = schools
      .map(s => s.id)
      .filter(id => id !== removingId);

    return UserActions.update({ organization_ids: schoolIds }).then(
      onRemoveSuccess,
      ToastActions.showDefaultErrorToast,
    );
  },

  addParent(name: string, email: string) {
    return User.addParent(name, email).then(user => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
      return user;
    });
  },
};

export default UserActions;



// WEBPACK FOOTER //
// ./src/user/UserActions.js